import React, { useState, useEffect, useRef } from 'react'
import './output.css'
import './aiActionPanel.css'
import PortValuesRangeMapping from '../../../../../logic/PortValuesRangeMapping'
import { getAiCodeFeatures } from '../../../../../../hooks/useBrandConfig'
import SegmentedControl from '../../../../../ReusableComponents/SegmentedControl/SegmentedControl'
import ScrollDots from '../../../../../ReusableComponents/ScrollDots/ScrollDots'
import CollapseItem from '../../../../../ReusableComponents/CollapseItem/CollapseItem'
import useStableKeys from '../../../../../../hooks/useStableKeys'
import {
    loadOutputs,
    instancesOfType,
} from '../../../../../WebAi/outputInstances'
import {
    newMediaKey,
    putMedia,
    deleteMedia,
} from '../../../../../WebAi/mediaStore'
import {
    loadEnabledVars,
    varById,
    opsFor,
    ACTION_MODES,
    assignSources,
} from '../../../../../WebAi/aiVariables'
import {
    authoringDevice,
    deviceLabel,
    accessoriesFor,
    portsFor,
    servosFor,
    servoById,
    hasServos,
    ACCESSORY_LABELS,
} from '../../../../../WebAi/deviceCapabilities'
import { X, Plus } from 'lucide-react'

// Sentinel accessory value meaning "a robot smart/PWM servo" (Humanoid/Hexapod/
// Klaw) rather than a port+accessory on a normal board.
const SERVO_ACC = '__servo__'

// AI-code action panel for a flowchart "output" node. Supports MULTIPLE actions
// (add/remove). Each action is either a hardware output (port + accessory +
// value, sent as live bytes on Execute) or a "Display Text" action (shown on
// the Execute screen at its Layout position). Stored as allDetails[id].actions.
// Value ranges. Motors run -100..+100 (direction; default 0). The 360°
// continuous servo is a speed -100..0..+100. 180/270/SG90 are position servos.
const LIVE_WRITE = [
    { type: 'led', label: 'LED', min: 0, max: 100 },
    { type: 'led_1c', label: 'LED 1C', min: 0, max: 100 },
    { type: 'PWM_3_Pin_Output', label: 'PWM 3-Pin Output', min: 0, max: 100 },
    { type: 'dc_motor', label: 'DC Motor', min: -100, max: 100 },
    // { type: 'geared_motor', label: 'Geared Motor', min: -100, max: 100 },
    { type: 'BO_Motor', label: 'BO Motor', min: -100, max: 100 },
    {
        type: 'mini_geared_motor',
        label: 'Mini Geared Motor',
        min: -100,
        max: 100,
    },
    { type: 'dynamex_motor', label: 'Dynamex Motor', min: -100, max: 100 },
    { type: 'servo_motor', label: 'SERVO', min: 0, max: 180 },
    { type: 'servo_motor_270', label: 'SERVO_270', min: 0, max: 270 },
    { type: 'servo_motor_sg90', label: 'SERVO_SG90', min: 0, max: 180 },
    { type: 'servo_motor_360', label: 'SERVO_360', min: -100, max: 100 },
]
const newHardware = (accessory = '', port = '') => ({
    kind: 'hardware',
    accessory,
    port,
    value: 0,
})

// Display actions target a specific output INSTANCE (Text 1, Image 2, ...) that
// was placed in the Configure > Output tab.
const DISPLAY = ['text', 'image', 'video', 'audio']

const newVariable = (varId) => ({
    kind: 'variable',
    varId,
    op: opsFor(varById(varId)?.kind || 'counter')[0].value,
    mode: 'value',
    value: 0,
    assignRef: 'count',
})

const AiActionPanel = (props) => {
    const id = props.check

    // Device-aware hardware: the connected/chosen device drives which
    // accessories, ports and robot servos the hardware action can target.
    const device = authoringDevice()
    const devLabel = deviceLabel()
    const accList = accessoriesFor(device)
    const deviceServos = servosFor(device)
    const deviceHasServos = hasServos(device)
    // Accessory options for the dropdown: normal accessories + a "Robot Servo"
    // entry when the device has smart/PWM servos.
    const accessoryOptions = [
        ...accList.map((a) => ({
            value: a,
            label: ACCESSORY_LABELS[a] || a,
        })),
        ...(deviceHasServos
            ? [{ value: SERVO_ACC, label: 'Serial Servo' }]
            : []),
    ]
    // A new hardware action starts with NOTHING selected — the user picks an
    // accessory first (which then reveals the valid ports).
    const defaultHardware = () => newHardware('', '')

    // Counters/flags enabled in the Start block, assignable as an action.
    const enabledVars = loadEnabledVars()
    // "Object count" is only an assignable source when the Object feature was
    // chosen on the Select Feature screen.
    const objectFeatureSelected = (() => {
        try {
            const feats =
                JSON.parse(sessionStorage.getItem('aiCodeSelectedFeatures')) ||
                []
            return feats.includes('ai/object')
        } catch {
            return false
        }
    })()
    // Whether a microphone input is selected — enables the live-speech assign
    // source (text only).
    const micSelected = (() => {
        try {
            const feats =
                JSON.parse(sessionStorage.getItem('aiCodeSelectedFeatures')) ||
                []
            return feats.includes('inputs/microphone')
        } catch {
            return false
        }
    })()
    // Numeric assign sources (for counters) and text assign sources (for a Text
    // output, which additionally can print the live speech transcript).
    const assignOptions = assignSources({
        hasObjectCount: objectFeatureSelected,
        vars: enabledVars,
    })
    const textAssignOptions = assignSources({
        hasObjectCount: objectFeatureSelected,
        hasSpeech: micSelected,
        vars: enabledVars,
    })
    const firstAssignRef = assignOptions[0]?.value || 'count'
    const firstTextAssignRef = textAssignOptions[0]?.value || 'count'

    // Which "display" outputs are enabled on the Select Feature screen — the
    // matching toggle button is only shown when its output is enabled.
    const outputsEnabled = {}
    getAiCodeFeatures('outputs').forEach((f) => {
        outputsEnabled[f.slug] = f.enabled !== false
    })
    // Output instances placed in Configure > Output, grouped by type.
    const allOutputs = loadOutputs()
    const KIND_BUTTONS = [
        { kind: 'hardware', label: devLabel, show: true },
        {
            kind: 'text',
            label: 'Text',
            show:
                !!outputsEnabled.text &&
                instancesOfType(allOutputs, 'text').length > 0,
        },
        {
            kind: 'image',
            label: 'Image',
            show:
                !!outputsEnabled.image &&
                instancesOfType(allOutputs, 'image').length > 0,
        },
        {
            kind: 'video',
            label: 'Video',
            show:
                !!outputsEnabled.video &&
                instancesOfType(allOutputs, 'video').length > 0,
        },
        {
            kind: 'audio',
            label: 'Audio',
            show:
                !!outputsEnabled.audio &&
                instancesOfType(allOutputs, 'audio').length > 0,
        },
        {
            kind: 'variable',
            label: 'Counter / Flag',
            show: enabledVars.length > 0,
        },
    ]

    const [saveError, setSaveError] = useState('')
    // The sidebar selects a KIND (hardware / text / image / ...); the detail
    // edits the action(s) of that kind. Default to the first saved action's kind.
    const [selectedKind, setSelectedKind] = useState(() => {
        try {
            const all = JSON.parse(sessionStorage.getItem('allDetails')) || {}
            const saved = all[id]?.actions
            if (Array.isArray(saved) && saved[0]?.kind) return saved[0].kind
        } catch {
            /* ignore */
        }
        return 'hardware'
    })
    const [actions, setActions] = useState(() => {
        try {
            const all = JSON.parse(sessionStorage.getItem('allDetails')) || {}
            const saved = all[id]?.actions
            if (Array.isArray(saved) && saved.length) return saved
        } catch {
            /* ignore */
        }
        return [defaultHardware()]
    })

    const [actionKeys, dropActionKey] = useStableKeys(actions.length, 'act')
    const [leavingKeys, setLeavingKeys] = useState([])
    const [freshKey, setFreshKey] = useState(null)
    const detailRef = useRef(null)
    const scrollToEndRef = useRef(false)

    const persist = (next) => {
        setActions(next)
        const all = JSON.parse(sessionStorage.getItem('allDetails')) || {}
        all[id] = { ...(all[id] || { id }), actions: next }
        try {
            sessionStorage.setItem('allDetails', JSON.stringify(all))
            setSaveError('')
        } catch (e) {
            // A large image/video data URL can blow the storage quota. Don't
            // crash the whole app — keep the prior saved state and warn.
            setSaveError(
                'That file is too large to save. Please use a smaller image or video.'
            )
            console.log('Failed to save actions for node', id, e)
        }
    }

    // Persist the current (possibly default) actions on mount so they run even
    // if the user doesn't change anything. Also repair any variable action whose
    // variable was turned off (or replaced) in Start — repoint it to a valid
    // enabled variable (with a valid op), or drop back to a hardware action when
    // no variables remain (bug 47).
    useEffect(() => {
        const enabledIds = new Set(enabledVars.map((v) => v.id))
        let changed = false
        const repaired = actions.map((a) => {
            if (a.kind !== 'variable') return a
            if (a.varId && enabledIds.has(a.varId)) return a
            changed = true
            const first = enabledVars[0]
            if (!first) return defaultHardware()
            const ops = opsFor(first.kind)
            return { ...a, varId: first.id, op: ops[0].value }
        })
        persist(changed ? repaired : actions)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const update = (i, patch) =>
        persist(actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)))
    // ---- Hardware port occupancy (mirrors the Assembly connection model) ----
    // Motor types drive a full H-bridge, so a motor on socket "A" physically
    // uses BOTH pins A1 and A2 — it conflicts with anything on either pin. Every
    // other accessory (LED/servo) occupies only its own pin, so a servo on A1 and
    // an LED on A2 can coexist. Dedicated motor sockets (M1/M2) occupy themselves.
    const MOTOR_ACCS = new Set([
        'dc_motor',
        'geared_motor',
        'mini_geared_motor',
        'dynamex_motor',
    ])
    // Physical pins a given accessory would occupy at a port.
    const pinsOf = (port, accessory) => {
        if (!port) return []
        if (MOTOR_ACCS.has(accessory)) {
            // Group socket A..F (or its pin form A1/B1…) reserves both pins.
            const m = /^([A-F])(?:[12])?$/.exec(port)
            if (m) return [`${m[1]}1`, `${m[1]}2`]
        }
        return [port]
    }

    // Pins driven by OTHER actions in THIS block — a physical pin can be driven
    // only once per block.
    const pinsUsedHere = (except) => {
        const set = new Set()
        actions.forEach((a, idx) => {
            if (
                idx !== except &&
                a.kind === 'hardware' &&
                a.accessory &&
                a.accessory !== SERVO_ACC &&
                a.port
            )
                pinsOf(a.port, a.accessory).forEach((p) => set.add(p))
        })
        return set
    }
    // Across OTHER live blocks: which accessory TYPE owns each pin. A physical
    // port drives one device, so a DIFFERENT accessory type can't reuse an
    // occupied pin. The SAME type is allowed — either reusing that device from
    // another block, or a second identical device on its own free port — so the
    // same component type can be used multiple times on different ports (bug 35).
    const pinOwnersOtherBlocks = () => {
        const pinOwner = {} // pin -> accessory type
        const add = (a) => {
            if (
                !a ||
                a.kind !== 'hardware' ||
                !a.accessory ||
                a.accessory === SERVO_ACC ||
                !a.port
            )
                return
            pinsOf(a.port, a.accessory).forEach((p) => {
                if (!(p in pinOwner)) pinOwner[p] = a.accessory
            })
        }
        try {
            const all = JSON.parse(sessionStorage.getItem('allDetails')) || {}
            // Only nodes that still exist in the flowchart own a port. A deleted
            // block leaves a stale allDetails entry behind, which would otherwise
            // keep its port reserved forever ("A is in use" after removal).
            const elements =
                JSON.parse(sessionStorage.getItem('flowchart-elements')) || []
            const liveIds = new Set(
                elements
                    .filter((e) => e && e.id != null)
                    .map((e) => String(e.id))
            )
            Object.entries(all).forEach(([nodeId, detail]) => {
                if (String(nodeId) === String(id)) return
                if (!liveIds.has(String(nodeId))) return
                ;(detail?.actions || []).forEach(add)
            })
        } catch {
            /* ignore */
        }
        return pinOwner
    }
    // A port is unavailable for `accessory` when any pin it would occupy is
    // already driven in THIS block (any type — one device per pin), or owned by a
    // DIFFERENT accessory type in another block. The SAME type stays available so
    // it can be reused, or placed a second time on a different free port.
    const portTaken = (except, port, accessory) => {
        const pins = pinsOf(port, accessory)
        const here = pinsUsedHere(except)
        const pinOwner = pinOwnersOtherBlocks()
        return pins.some((p) => {
            if (here.has(p)) return true
            const owner = pinOwner[p]
            return !!owner && owner !== accessory
        })
    }
    // Default port for a freshly picked accessory: the first socket that is fully
    // free (nothing on any of its pins, here or elsewhere) so a second identical
    // device lands on its own port; fall back to any non-conflicting socket.
    const firstFreePort = (except, ports, accessory) => {
        const here = pinsUsedHere(except)
        const pinOwner = pinOwnersOtherBlocks()
        const fullyFree = (p) =>
            pinsOf(p, accessory).every(
                (pin) => !here.has(pin) && !pinOwner[pin]
            )
        // No ports[0] fallback: when every port is taken, return '' rather than
        // silently re-using an occupied port (two devices on one port).
        return (
            ports.find(fullyFree) ||
            ports.find((p) => !portTaken(except, p, accessory)) ||
            ''
        )
    }
    // True while at least one port is still assignable for `accessory` in this
    // block. When false, picking it would only collide with a used port, so the
    // accessory is disabled in the dropdown.
    const accessoryHasFreePort = (except, accessory) =>
        portsFor(device, accessory).some(
            (p) => !portTaken(except, p, accessory)
        )
    // Output instances of `kind` not already targeted by another action.
    const freeInstances = (kind, except) => {
        const used = actions
            .filter((a, idx) => idx !== except && a.kind === kind)
            .map((a) => a.outputId)
        return instancesOfType(allOutputs, kind).filter(
            (o) => !used.includes(o.id)
        )
    }
    // Counters/flags already targeted by another variable action (each may be
    // assigned only once per node, like a hardware port).
    const usedVars = (except) =>
        actions
            .filter((a, idx) => idx !== except && a.kind === 'variable')
            .map((a) => a.varId)
    const freeVars = (except) => {
        const used = usedVars(except)
        return enabledVars.filter((v) => !used.includes(v.id))
    }
    // A fresh action of a given kind, with sensible defaults for its target.
    const makeAction = (kind) => {
        if (kind === 'hardware') return defaultHardware()
        if (kind === 'variable') {
            const v = freeVars(-1)[0] || enabledVars[0]
            return newVariable(v?.id)
        }
        const free = freeInstances(kind, -1)
        if (kind === 'text')
            return {
                kind,
                outputId: free[0]?.id,
                text: '',
                mode: 'value',
                assignRef: firstTextAssignRef,
            }
        return { kind, outputId: free[0]?.id, src: '', name: '' }
    }

    // Selecting a kind tab shows its editor; create one action of that kind if
    // none exists yet so the editor has something to edit.
    const ensureKind = (kind) => {
        if (actions.some((a) => a.kind === kind)) return
        persist([...actions, makeAction(kind)])
    }
    const selectKind = (kind) => {
        setSelectedKind(kind)
        ensureKind(kind)
    }
    // Whether another action of `kind` can still be added: display kinds are
    // capped by how many output instances were placed (Text 1, Text 2, ...),
    // variables by how many counters/flags are enabled. Hardware has many ports,
    // so it's only exhausted when every port of every accessory is taken.
    const kindExhausted = (kind) => {
        if (kind === 'variable') return freeVars(-1).length === 0
        if (DISPLAY.includes(kind)) return freeInstances(kind, -1).length === 0
        return false
    }
    // "+" adds another action of the current kind, duplicating the type but
    // targeting the next free instance/variable (or a fresh hardware port).
    const addOfKind = (kind) => {
        if (kindExhausted(kind)) return
        scrollToEndRef.current = true
        persist([...actions, makeAction(kind)])
    }
    const removeAction = (i) => {
        // Free the removed action's IndexedDB media blob, if any.
        const gone = actions[i]?.mediaKey
        if (gone) deleteMedia(gone)
        dropActionKey(i)
        persist(actions.filter((_, idx) => idx !== i))
    }
    const startRemove = (key) =>
        setLeavingKeys((ks) => (ks.includes(key) ? ks : [...ks, key]))
    const finishRemove = (key) => {
        setLeavingKeys((ks) => ks.filter((k) => k !== key))
        removeAction(actionKeys.indexOf(key))
    }

    // A short title for each action card so stacked actions are distinguishable.
    const cardTitle = (aAction) => {
        if (aAction.kind === 'hardware') {
            if (aAction.accessory === SERVO_ACC)
                return (
                    servoById(device, aAction.servoId)?.name || 'Serial Servo'
                )
            if (aAction.accessory)
                return `${
                    ACCESSORY_LABELS[aAction.accessory] || aAction.accessory
                }${aAction.port ? ` · ${aAction.port}` : ''}`
            return devLabel
        }
        if (aAction.kind === 'variable')
            return varById(aAction.varId)?.name || 'Counter / Flag'
        const inst = instancesOfType(allOutputs, aAction.kind).find(
            (o) => o.id === aAction.outputId
        )
        return (
            inst?.name ||
            KIND_BUTTONS.find((k) => k.kind === aAction.kind)?.label ||
            aAction.kind
        )
    }
    // Store a picked media file so Execute can display/play it. Images are
    // downscaled to a small JPEG data URL kept inline in allDetails; audio/video
    // blobs go to IndexedDB (mediaStore) with only a key kept inline, because a
    // multi-MB clip's data URL blows sessionStorage's ~5MB quota.
    const onPickMedia = (i, file) => {
        if (!file) return
        if (file.type && file.type.startsWith('image/')) {
            const url = URL.createObjectURL(file)
            const img = new Image()
            img.onload = () => {
                const max = 640
                const scale = Math.min(
                    1,
                    max / Math.max(img.width || 1, img.height || 1)
                )
                const w = Math.max(1, Math.round((img.width || 1) * scale))
                const h = Math.max(1, Math.round((img.height || 1) * scale))
                const c = document.createElement('canvas')
                c.width = w
                c.height = h
                c.getContext('2d').drawImage(img, 0, 0, w, h)
                URL.revokeObjectURL(url)
                update(i, {
                    src: c.toDataURL('image/jpeg', 0.82),
                    name: file.name,
                })
            }
            img.onerror = () => {
                URL.revokeObjectURL(url)
                setSaveError('Could not read that image.')
            }
            img.src = url
            return
        }
        // Audio/video clips are far too large for sessionStorage's ~5MB quota,
        // so store the raw blob in IndexedDB and keep only a lightweight key in
        // allDetails. Execute resolves the key back to a playable object URL.
        const prevKey = actions[i]?.mediaKey
        const key = newMediaKey(id)
        putMedia(key, file)
            .then(() => {
                if (prevKey) deleteMedia(prevKey)
                update(i, { mediaKey: key, name: file.name, src: '' })
            })
            .catch((e) => {
                console.log('Failed to store media', id, e)
                setSaveError('Could not save that file. Please try again.')
            })
    }

    // Value range for a port+accessory, taken from the shared
    // PortValuesRangeMapping (device/version aware). Falls back to the static
    // LIVE_WRITE ranges if the port/accessory isn't mapped.
    const rangeFor = (port, accessory) => {
        const fn = PortValuesRangeMapping?.[port]?.[accessory]
        if (typeof fn === 'function') {
            try {
                const r = fn(port)
                if (r && typeof r.min === 'number' && typeof r.max === 'number')
                    return { min: r.min, max: r.max }
            } catch {
                /* ignore and fall through */
            }
        }
        const f = LIVE_WRITE.find((a) => a.type === accessory)
        return f ? { min: f.min, max: f.max } : { min: 0, max: 255 }
    }

    // Sensible starting value: 0 when it's within range (so motors/servo-360
    // start stopped, not at -100), else the low end. Fixes motors defaulting to
    // -100 on selection.
    const defaultValueFor = (range) =>
        range.min <= 0 && range.max >= 0 ? 0 : range.min

    // Shared "Value | Assign" picker: a Value tab renders `literalNode` (a typed
    // number/text); an Assign tab renders a dropdown of live sources (object
    // count + variables). Used by both the counter and the text actions.
    const renderModeField = (a, i, literalNode, options, firstRef) => {
        const mode = a.mode || 'value'
        const opts = options || assignOptions
        return (
            <div className="AiActionPanel_field AiActionPanel_modeField">
                <SegmentedControl
                    value={mode}
                    onChange={(m) => update(i, { mode: m })}
                    options={ACTION_MODES.map((o) => ({
                        value: o.value,
                        label: o.label,
                        disabled: o.value === 'assign' && opts.length === 0,
                        disabledHint: 'No live source available',
                    }))}
                />
                {mode === 'assign' ? (
                    <select
                        className="AiActionPanel_select"
                        value={a.assignRef || firstRef || firstAssignRef}
                        onChange={(e) =>
                            update(i, { assignRef: e.target.value })
                        }
                    >
                        {opts.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                ) : (
                    literalNode
                )}
            </div>
        )
    }

    const kindShown = KIND_BUTTONS.filter((b) => b.show)
    const activeKind = kindShown.some((b) => b.kind === selectedKind)
        ? selectedKind
        : kindShown[0]?.kind || 'hardware'
    const kindActions = actions
        .map((act, idx) => ({ act, idx }))
        .filter(({ act }) => act.kind === activeKind)

    useEffect(() => {
        if (!scrollToEndRef.current) return undefined
        scrollToEndRef.current = false
        setFreshKey(actionKeys[actions.length - 1])
        const frame = requestAnimationFrame(() => {
            const el = detailRef.current
            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
        })
        return () => cancelAnimationFrame(frame)
    }, [actions.length])

    useEffect(() => {
        setFreshKey(null)
        if (detailRef.current) detailRef.current.scrollTop = 0
    }, [activeKind])

    // One action's editor: the output target (display kinds) + the kind-specific
    // fields. `i` is the action's index in `actions`.
    const renderActionEditor = (a, i) => {
        const isServoAction = a.kind === 'hardware' && a.accessory === SERVO_ACC
        const servo = isServoAction ? servoById(device, a.servoId) : null
        const acc = isServoAction
            ? { min: servo?.min ?? 0, max: servo?.max ?? 180 }
            : rangeFor(a.port, a.accessory)
        return (
            <>
                {/* Which placed output instance this action targets. */}
                {DISPLAY.includes(a.kind) && (
                    <div className="AiActionPanel_row">
                        <span className="AiActionPanel_fieldLabel">Output</span>
                        <select
                            className="AiActionPanel_select"
                            value={a.outputId || ''}
                            onChange={(e) =>
                                update(i, {
                                    outputId: e.target.value,
                                })
                            }
                        >
                            {instancesOfType(allOutputs, a.kind).map((o) => {
                                const taken = freeInstances(a.kind, i).every(
                                    (f) => f.id !== o.id
                                )
                                const isSelf = o.id === a.outputId
                                return (
                                    <option
                                        key={o.id}
                                        value={o.id}
                                        disabled={taken && !isSelf}
                                    >
                                        {o.name}
                                        {taken && !isSelf ? ' (in use)' : ''}
                                    </option>
                                )
                            })}
                        </select>
                    </div>
                )}

                {a.kind === 'text' ? (
                    <div className="AiActionPanel_row">
                        <span className="AiActionPanel_fieldLabel">Text</span>
                        {renderModeField(
                            a,
                            i,
                            <input
                                className="AiActionPanel_textInput"
                                type="text"
                                placeholder="Enter text (position it in Layout)"
                                value={a.text || ''}
                                onChange={(e) =>
                                    update(i, {
                                        text: e.target.value,
                                    })
                                }
                            />,
                            textAssignOptions,
                            firstTextAssignRef
                        )}
                    </div>
                ) : a.kind === 'image' ||
                  a.kind === 'video' ||
                  a.kind === 'audio' ? (
                    <div className="AiActionPanel_row">
                        <span className="AiActionPanel_fieldLabel">
                            {a.kind === 'image'
                                ? 'Image'
                                : a.kind === 'video'
                                  ? 'Video'
                                  : 'Audio'}
                        </span>
                        <input
                            type="file"
                            id={`ai-action-media-${id}-${i}`}
                            hidden
                            accept={
                                a.kind === 'image'
                                    ? 'image/*'
                                    : a.kind === 'video'
                                      ? 'video/*'
                                      : 'audio/*'
                            }
                            onChange={(e) =>
                                onPickMedia(i, e.target.files?.[0])
                            }
                        />
                        <label
                            htmlFor={`ai-action-media-${id}-${i}`}
                            className="AiActionPanel_upload"
                        >
                            {a.name ||
                                `Upload ${a.kind} (position it in Layout)`}
                        </label>
                    </div>
                ) : a.kind === 'variable' ? (
                    (() => {
                        const vk = varById(a.varId)?.kind || 'counter'
                        const ops = opsFor(vk)
                        return (
                            <>
                                <div className="AiActionPanel_topRow">
                                    <div className="AiActionPanel_field">
                                        <span className="AiActionPanel_fieldLabel">
                                            Variable
                                        </span>
                                        <select
                                            className="AiActionPanel_select"
                                            value={a.varId || ''}
                                            onChange={(e) => {
                                                const nk =
                                                    varById(e.target.value)
                                                        ?.kind || 'counter'
                                                const validOps = opsFor(nk)
                                                update(i, {
                                                    varId: e.target.value,
                                                    op: validOps.some(
                                                        (o) => o.value === a.op
                                                    )
                                                        ? a.op
                                                        : validOps[0].value,
                                                })
                                            }}
                                        >
                                            {enabledVars.map((v) => {
                                                const taken = usedVars(
                                                    i
                                                ).includes(v.id)
                                                const isSelf = v.id === a.varId
                                                return (
                                                    <option
                                                        key={v.id}
                                                        value={v.id}
                                                        disabled={
                                                            taken && !isSelf
                                                        }
                                                    >
                                                        {v.name}
                                                        {taken && !isSelf
                                                            ? ' (in use)'
                                                            : ''}
                                                    </option>
                                                )
                                            })}
                                        </select>
                                    </div>
                                    <div className="AiActionPanel_field">
                                        <span className="AiActionPanel_fieldLabel">
                                            Operation
                                        </span>
                                        <select
                                            className="AiActionPanel_select"
                                            value={a.op || 'set'}
                                            onChange={(e) =>
                                                update(i, {
                                                    op: e.target.value,
                                                })
                                            }
                                        >
                                            {ops.map((o) => (
                                                <option
                                                    key={o.value}
                                                    value={o.value}
                                                >
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                {vk === 'flag' ? (
                                    a.op !== 'toggle' && (
                                        <div className="AiActionPanel_row">
                                            <span className="AiActionPanel_fieldLabel">
                                                Value
                                            </span>
                                            <select
                                                className="AiActionPanel_select"
                                                value={
                                                    Number(a.value) ? '1' : '0'
                                                }
                                                onChange={(e) =>
                                                    update(i, {
                                                        value: Number(
                                                            e.target.value
                                                        ),
                                                    })
                                                }
                                            >
                                                <option value="1">On</option>
                                                <option value="0">Off</option>
                                            </select>
                                        </div>
                                    )
                                ) : vk === 'counter' ? (
                                    // A counter only steps +1/-1 — the
                                    // operation is the whole action.
                                    <div className="AiActionPanel_row">
                                        <span className="AiActionPanel_fieldLabel">
                                            Steps each time it runs
                                        </span>
                                    </div>
                                ) : (
                                    // Data: assign/add/subtract a value or
                                    // another source (never itself).
                                    <div className="AiActionPanel_row">
                                        <span className="AiActionPanel_fieldLabel">
                                            Value
                                        </span>
                                        {renderModeField(
                                            a,
                                            i,
                                            <input
                                                className="AiActionPanel_textInput"
                                                type="number"
                                                value={a.value ?? 0}
                                                onChange={(e) =>
                                                    update(i, {
                                                        value: e.target.value,
                                                    })
                                                }
                                            />,
                                            assignOptions.filter(
                                                (o) => o.value !== a.varId
                                            )
                                        )}
                                    </div>
                                )}
                            </>
                        )
                    })()
                ) : accessoryOptions.length === 0 ? (
                    <div className="AiActionPanel_row">
                        <span className="AiActionPanel_fieldLabel">
                            No hardware controls configured for {devLabel}.
                        </span>
                    </div>
                ) : (
                    <>
                        <div className="AiActionPanel_topRow">
                            {/* Accessory first — it decides which
                                            ports (or servos) are valid. */}
                            <div className="AiActionPanel_field">
                                <span className="AiActionPanel_fieldLabel">
                                    Accessory
                                </span>
                                <select
                                    className="AiActionPanel_select"
                                    value={a.accessory || ''}
                                    onChange={(e) => {
                                        const v = e.target.value
                                        if (v === '') {
                                            update(i, {
                                                accessory: '',
                                                port: '',
                                                value: 0,
                                            })
                                        } else if (v === SERVO_ACC) {
                                            const s = deviceServos[0]
                                            update(i, {
                                                accessory: v,
                                                servoId: s?.id,
                                                value:
                                                    s?.default ?? s?.min ?? 0,
                                            })
                                        } else {
                                            const ports = portsFor(device, v)
                                            const port = firstFreePort(
                                                i,
                                                ports,
                                                v
                                            )
                                            update(i, {
                                                accessory: v,
                                                port,
                                                value: defaultValueFor(
                                                    rangeFor(port, v)
                                                ),
                                            })
                                        }
                                    }}
                                >
                                    <option value="">Select accessory</option>
                                    {accessoryOptions.map((o) => {
                                        // Disable an accessory once every one of
                                        // its ports is occupied in this block —
                                        // otherwise it would re-use a taken port.
                                        const exhausted =
                                            o.value !== SERVO_ACC &&
                                            o.value !== a.accessory &&
                                            !accessoryHasFreePort(i, o.value)
                                        return (
                                            <option
                                                key={o.value}
                                                value={o.value}
                                                disabled={exhausted}
                                            >
                                                {o.label}
                                                {exhausted
                                                    ? ' (ports full)'
                                                    : ''}
                                            </option>
                                        )
                                    })}
                                </select>
                            </div>
                            {a.accessory === SERVO_ACC ? (
                                <div className="AiActionPanel_field">
                                    <span className="AiActionPanel_fieldLabel">
                                        Servo
                                    </span>
                                    <select
                                        className="AiActionPanel_select"
                                        value={String(a.servoId ?? '')}
                                        onChange={(e) => {
                                            const s = servoById(
                                                device,
                                                e.target.value
                                            )
                                            update(i, {
                                                servoId: e.target.value,
                                                value:
                                                    s?.default ?? s?.min ?? 0,
                                            })
                                        }}
                                    >
                                        {deviceServos.map((s) => (
                                            <option key={s.id} value={s.id}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            ) : a.accessory ? (
                                <div className="AiActionPanel_field">
                                    <span className="AiActionPanel_fieldLabel">
                                        Port
                                    </span>
                                    <select
                                        className="AiActionPanel_select"
                                        value={a.port || ''}
                                        onChange={(e) =>
                                            update(i, {
                                                port: e.target.value,
                                                value: defaultValueFor(
                                                    rangeFor(
                                                        e.target.value,
                                                        a.accessory
                                                    )
                                                ),
                                            })
                                        }
                                    >
                                        {portsFor(device, a.accessory).map(
                                            (p) => {
                                                // Taken if used by
                                                // another action here, or
                                                // owned by a different
                                                // accessory elsewhere.
                                                const taken = portTaken(
                                                    i,
                                                    p,
                                                    a.accessory
                                                )
                                                const isSelf = p === a.port
                                                return (
                                                    <option
                                                        key={p}
                                                        value={p}
                                                        disabled={
                                                            taken && !isSelf
                                                        }
                                                    >
                                                        {p}
                                                        {taken && !isSelf
                                                            ? ' (in use)'
                                                            : ''}
                                                    </option>
                                                )
                                            }
                                        )}
                                    </select>
                                </div>
                            ) : null}
                        </div>
                        {a.accessory && (
                            <div className="AiActionPanel_valueBlock">
                                <span className="AiActionPanel_fieldLabel">
                                    Value
                                </span>
                                <input
                                    className="AiActionPanel_slider"
                                    type="range"
                                    min={acc.min}
                                    max={acc.max}
                                    value={a.value}
                                    onChange={(e) =>
                                        update(i, {
                                            value: e.target.value,
                                        })
                                    }
                                />
                                <div className="AiActionPanel_range">
                                    <span>{acc.min}</span>
                                    <span className="AiActionPanel_current">
                                        {a.value}
                                    </span>
                                    <span>{acc.max}</span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </>
        )
    }

    return (
        <div className="outertabDiv-output AiActionPanel_shell">
            <button
                className="AiActionPanel_close"
                onClick={props.handleClose}
                title="Close"
                aria-label="Close"
            >
                <X />
            </button>
            {saveError && (
                <div className="AiActionPanel_saveError">{saveError}</div>
            )}
            <div className="AiActionPanel_body">
                <div className="AiActionPanel_rail">
                    {kindShown.map((b) => (
                        <button
                            key={b.kind}
                            type="button"
                            className={`AiActionPanel_pill ${
                                b.kind === activeKind
                                    ? 'AiActionPanel_pillOn'
                                    : ''
                            }`}
                            onClick={() => selectKind(b.kind)}
                        >
                            <span className="AiActionPanel_pillLabel">
                                {b.label}
                            </span>
                        </button>
                    ))}
                </div>
                <div className="AiActionPanel_detailWrap">
                    <div className="AiActionPanel_detail" ref={detailRef}>
                        {kindActions.length === 0 ? (
                            <div className="AiActionPanel_row">
                                <span className="AiActionPanel_fieldLabel">
                                    No {activeKind} action yet.
                                </span>
                            </div>
                        ) : (
                            kindActions.map(({ act, idx }) => {
                                const key = actionKeys[idx]
                                const leaving = leavingKeys.includes(key)
                                return (
                                    <CollapseItem
                                        key={key}
                                        className="AiActionPanel_card"
                                        gap="3vh"
                                        leaving={leaving}
                                        highlight={key === freshKey}
                                        onLeft={() => finishRemove(key)}
                                    >
                                        <div className="AiActionPanel_cardHead">
                                            <span className="AiActionPanel_cardTitle">
                                                {cardTitle(act)}
                                            </span>
                                            <button
                                                className="AiActionPanel_remove"
                                                onClick={() => startRemove(key)}
                                                disabled={leaving}
                                                title="Remove action"
                                            >
                                                <X />
                                            </button>
                                        </div>
                                        {renderActionEditor(act, idx)}
                                    </CollapseItem>
                                )
                            })
                        )}
                    </div>
                    <ScrollDots
                        targetRef={detailRef}
                        watch={`${activeKind}:${kindActions.length}:${leavingKeys.length}`}
                        className="AiActionPanel_dots"
                    />
                </div>
            </div>

            <button
                className="AiActionPanel_add"
                onClick={() => addOfKind(activeKind)}
                disabled={kindExhausted(activeKind)}
                title={
                    kindExhausted(activeKind)
                        ? 'No more to add'
                        : `Add ${activeKind} action`
                }
            >
                <Plus />
            </button>
        </div>
    )
}

export default AiActionPanel
